import {
    createControl,
    removeInvisibleCharsOnPaste,
    updateControl, validateControl,
    validateForm
} from '../../../domain/validation/validation';
import {ClipboardEvent, Dispatch, KeyboardEvent, useEffect, useState} from 'react';
import { IServerValidation } from '../../../domain/validation/interfaces';
import {getSimpleCaptchaValidationError } from '../../../domain/captcha/simple-captcha';
import { getIsChina } from '../../../domain/hidden-data-provider';
import Preloader from '../../../ui/svg/common/preloader';
import ValidationErrors from '../../../domain/validation/server-errors/validation-errors';
import Recaptcha3Text from '../../../domain/captcha/recaptcha3-terms';
import {
    getRecaptcha2Error,
    getRecaptchaToken,
    renderRecaptcha2,
    resetRecaptcha2
} from '../../../domain/captcha/recaptcha-provider';
import { subscribeToMessageGears } from '../services/data-provider';
import { getPageNodeGUID } from '../../../domain/common';
import { getUTMFromStorage } from '../../../domain/marketing/utm/utm-provider';
import { tiktokLead } from '../../../domain/marketing/tiktok-provider';
import { facebookLead } from '../../../domain/facebook/facebook-marketing-provider';
import { saveNodeGUID } from '../services/message-gears-provider';
import { IMessageGearsForm, ISubscribeResponse } from '../interfaces';
import { IFormControl } from '../../../ui/forms/interfaces';
import FormTextField from '../../../ui/forms/fields/text-field';
import ChinaCaptchaField from '../../../ui/forms/fields/captcha-china-field';
import { removeQueryParamFromUrl } from '../../../domain/url-provider';
import { outbrainLead } from '../../../domain/marketing/outbrain-provider';

export const getMsgrsEmail = (rguidEmail?: string|null) => {
    if(rguidEmail) {
        // https://dev.waves.com/zzzzz?id=1&rguid=066585E7-61E9-41A5-B5DD-534AE51C69FF&zz=xx
        removeQueryParamFromUrl('rguid');
        return rguidEmail.trim();
    }

    const $el = document.getElementById('waves-user-email');
    if(!$el || !$el.textContent) return '';
    return $el.textContent.trim();
};

/**
 * Message Gears Form
 * https://wavesaudio.atlassian.net/browse/DSWEB-1837
 * https://dev13.waves.com/newsletter
 */
const MessageGearsForm = (props: IMessageGearsForm) => {

    const isChina = getIsChina();

    // validation ---------------------------------------
    const emailControl = createControl('email', getMsgrsEmail(props.rguidEmail) || '', {
        required: true,
        email: true,
    });

    const firstNameControl = createControl('firstName', '', {
        name: true,
    });

    const lastNameControl = createControl('lastName', '', {
        name: true,
    });

    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [formStyles, setFormStyles] = useState({});
    const [nodeGuid] = useState(getPageNodeGUID());
    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [serverOKMessage, setServerOKMessage] = useState('');

    const [email, setEmail] = useState<IFormControl>(emailControl);
    const [firstName, setFirstName] = useState<IFormControl>(firstNameControl);
    const [lastName, setLastName] = useState<IFormControl>(lastNameControl);

    // simple captcha - used for chine
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    /**
     * set initial form styles
     */
    useEffect(() => {
        const styles: any = {
            width: props.width || '500px',
            maxWidth: '100%'
        };

        if(props.height){
            styles.height = props.height;
        }

        setFormStyles(styles);
    }, [props.width, props.height]);

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * client side validation fields
     */
    const getFormValidationFields = () => {
        const fields: ([IFormControl, Dispatch<any>])[] = [
            [email, setEmail]
        ];

        if(isChina){
            fields.push([simpleCaptchaCode, setSimpleCaptchaCode]);
        }

        if(props.showNames){
            fields.push([firstName, setFirstName]);
            fields.push([lastName, setLastName]);
        }

        return fields;
    };

    /**
     * subscribe to message gears
     */
    const subscribe = async () => {

        // client side validation
        const isValid = validateForm(getFormValidationFields());
        if(!isValid) return;

        setIsLoading(true);

        let response: {data: IServerValidation, statusCode: number }|null = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                return;
            }

            const utm = getUTMFromStorage();

            response = await subscribeToMessageGears(
                props.formName,
                email.value,
                captchaToken,
                recaptchaVersion,
                nodeGuid,
                utm,
                props.showNames ? firstName.value : '',
                props.showNames ? lastName.value : '',
            );
        }
        catch(ex){}

        // all is well ---------------
        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            tiktokLead();
            outbrainLead();
            facebookLead();

            if(props.ifSaveNodeGUID) {
                saveNodeGUID();
            }

            // close the popup
            if(props.isInPopup && props.ifShouldHideOnSubmit){
                setIsLoading(false);
                document.dispatchEvent(new CustomEvent('close-popup'));
                return;
            }

            const relatedData: ISubscribeResponse = response.data.relatedData ? response.data.relatedData : null;

            if(relatedData){

                if(relatedData.thankYouRdr){
                    window.location.href = relatedData.thankYouRdr;
                }
                else{
                    setIsLoading(false);
                    setServerOKMessage(relatedData.thankYouMsg);
                }
                return;
            }
        }

        // handle errors ---------
        setIsLoading(false);

        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await subscribe();
        }
    };

    return (
        <>
            {
                serverOKMessage && (
                    <div className="ok-box"><p>{ serverOKMessage }</p></div>
                )
            }

            {
                !serverOKMessage &&
                <div className={`flex flex-col ${ props.isCentered ? 'text-center justify-center' : '' }`} style={ formStyles }>

                    { props.children && <div dangerouslySetInnerHTML={{__html: props.children}} /> }

                    <FormTextField
                        id={ 'email-tb' }
                        label={ props.emailLabel || 'Email' }
                        control={ email }
                        maxLength={ 80 }
                        focus={ true }
                        rootClasses={ props.showNames ? 'mb-6' : '' }
                        onChange={ evt => {
                            const $target = evt.target as HTMLInputElement;
                            setEmail(updateControl(email, $target.value, !email.isValid));
                            setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={ evt => {
                            setEmail(validateControl(email));
                        }}
                        onClick={ handleFieldClick }
                        onKeyUp={ handleEnter }
                        onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                            setEmail(updateControl(email, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />

                    {
                        props.showNames && (
                            <>
                                <FormTextField
                                    id={ 'first-name-tb' }
                                    label={ 'First Name' }
                                    control={ firstName }
                                    maxLength={ 100 }
                                    onChange={ evt => {
                                        const $target = evt.target as HTMLInputElement;
                                        setFirstName(updateControl(firstName, $target.value, !firstName.isValid));
                                        setResponse(null); // empty server error
                                        setCaptchaError('');
                                    }}
                                    onBlur={ evt => {
                                        setFirstName(validateControl(firstName));
                                    }}
                                    onClick={ handleFieldClick }
                                    onKeyUp={ handleEnter }
                                    onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                        setFirstName(updateControl(firstName, removeInvisibleCharsOnPaste(evt)));
                                    }}
                                />

                                <FormTextField
                                    id={ 'last-name-tb' }
                                    label={ 'Last Name' }
                                    control={ lastName }
                                    maxLength={ 100 }
                                    onChange={ evt => {
                                        const $target = evt.target as HTMLInputElement;
                                        setLastName(updateControl(lastName, $target.value, !lastName.isValid));
                                        setResponse(null); // empty server error
                                        setCaptchaError('');
                                    }}
                                    onBlur={ evt => {
                                        setLastName(validateControl(lastName));
                                    }}
                                    onClick={ handleFieldClick }
                                    onKeyUp={ handleEnter }
                                    onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                        setLastName(updateControl(lastName, removeInvisibleCharsOnPaste(evt)));
                                    }}
                                />
                            </>
                        )
                    }

                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha" className={`mb-4 flex ${ props.isCentered ? 'justify-center' : '' } recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={ simpleCaptchaCode }
                            setSimpleCaptchaCode={ setSimpleCaptchaCode }
                            simpleCaptchaAttempts={ simpleCaptchaAttempts }
                        />
                    }

                    {
                        props.ifShowTerms &&
                        <p className={`text-xs my-4 ${ props.isCentered ? 'text-center' : '' }`}>By submitting this form, I agree that I have
                            read<br/> and accepted the <a href="/legal/privacy-policy" title="Waves Privacy Policy">Waves Privacy Policy</a>.</p>
                    }

                    <div className="mb-2">

                        {
                            isLoading &&
                            <div className={`flex ${ props.isCentered ? 'justify-center' : ''}`}>
                                <Preloader width="25px" height="25px" color="#4f4f4f" />
                            </div>
                        }

                        {
                            !isLoading &&
                            <button
                                className={`w-btn ${ props.isCentered ? 'mx-auto' : ''}`}
                                type="button"
                                style={{
                                    maxWidth: '200px'
                                }}
                                onClick={async evt => {
                                    await subscribe();
                                }}
                            >{ props.submitLabel }</button>
                        }
                    </div>

                    {
                        props.isInPopup &&
                        <div className={`text-xs mt-4 mb-4 ${ props.isCentered ? 'text-center' : '' }`}>
                            Don’t want to view this page?<br />
                            <a href="/" title="Visit Waves.com homepage">Visit Waves.com homepage</a>
                        </div>
                    }

                    {
                        response && !response.isValid && (
                            <div className={`text-sm text-red my-4 ${ props.isCentered ? 'text-center' : '' }`}>
                                <ValidationErrors errors={ response.errors } />
                            </div>
                        )
                    }

                    {
                        captchaError &&
                        <div data-id={ recaptcha2WidgetID } className={`text-sm text-red my-4 ${ props.isCentered ? 'text-center' : '' }`}>{ captchaError }</div>
                    }

                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className={`mt-8 ${ props.isCentered ? 'text-center' : ''}`}>
                            <Recaptcha3Text />
                        </div>
                    }
                </div>
            }
        </>
    )
};

export default MessageGearsForm;